import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

import { STUDIO_URL } from "./constants";
import PlaceholderPage from "./PlaceholderPage";
import { updatePreferences, getMe, fetchSystemInfo } from "../../api";
import { useLicense } from "../../LicenseContext";
import searchIconSrc from "../../assets/search.svg";
import halfMoonIconSrc from "../../assets/half-moon.svg";
import sunLightIconSrc from "../../assets/sun-light.svg";
import sparksIconSrc from "../../assets/sparks.svg";

const _gwConfig = (typeof window !== "undefined" && window.CS_GATEWAY_CONFIG) || {};
const CORE_URL = _gwConfig.CORE_URL || process.env.REACT_APP_CORE_URL || "http://localhost:8090";

/* Lazily flatten nav items for search — deferred to avoid circular dep
   (shared → navigation → sections → shared) */
let _navItemsCache = null;
function getAllNavItems() {
  if (!_navItemsCache) {
    const { NAV_GROUPS } = require("./navigation");
    _navItemsCache = NAV_GROUPS.flatMap((group) =>
      group.items.map((item) => ({
        ...item,
        group: group.title,
        searchText: `${group.title} ${item.label}`.toLowerCase(),
      }))
    );
  }
  return _navItemsCache;
}

// Same lazy-require trick as above — hasProductAccess lives in navigation.js
// too and pulling it in at module-load time would hit the same cycle.
function isProductVisible(item, products) {
  return require("./navigation").hasProductAccess(item, products);
}

const SidePanel = ({ title, items }) => (
  <aside className="gwSidePanel">
    <div className="gwSideTitle">{title}</div>
    <div className="gwSideList">
      {items.map((item, index) => (
        <button
          className={`gwSideItem ${index === 0 ? "isActive" : ""}`}
          key={item}
          type="button"
        >
          {item}
        </button>
      ))}
    </div>
  </aside>
);

function getInitialTheme() {
  try { return localStorage.getItem("gw-theme") || "dark"; } catch { return "dark"; }
}


const GatewayTopbar = ({ user: userProp, onNavigate, onMenuToggle, aiOpen, onToggleAI }) => {
  const [profileOpen, setProfileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [theme, setTheme] = useState(getInitialTheme);
  const { products, loading } = useLicense();

  const username = userProp?.username || "";
  const avatar = userProp?.avatar || "";

  /* Apply theme to root element and sync to the server. localStorage is a
     synchronous cache for flash-prevention (see public/index.html); the
     server is the source of truth, seeded by AuthGate on load. */
  const themeInitialMount = useRef(true);
  const skipNextThemePatch = useRef(false);
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    // OpenBridge components (charts/gauges) read their own theme attribute.
    // bright = true white, dusk = neutral dark gray. Deliberately NOT
    // day/night: OB's night palette is amber/brown (ship night vision)
    // and reads as an orange theme on a normal dashboard.
    document.documentElement.setAttribute("data-obc-theme", theme === "light" ? "bright" : "dusk");
    try { localStorage.setItem("gw-theme", theme); } catch {}
    if (themeInitialMount.current) {
      themeInitialMount.current = false;
      return;
    }
    if (skipNextThemePatch.current) {
      skipNextThemePatch.current = false;
      return;
    }
    updatePreferences({ theme }).catch(() => {
      /* Fire-and-forget. localStorage still holds the choice for this browser. */
    });
  }, [theme]);

  /* Pull the server's theme when this tab regains focus so a change made in
     another app (e.g. the editor) reaches us without a hard reload. */
  useEffect(() => {
    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const me = await getMe();
        const serverTheme = me?.preferences?.theme;
        if (serverTheme !== "light" && serverTheme !== "dark") return;
        if (serverTheme !== theme) {
          skipNextThemePatch.current = true;
          setTheme(serverTheme);
        }
      } catch {}
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  };
  const profileBtnRef = useRef(null);
  const profileMenuRef = useRef(null);
  const searchRef = useRef(null);
  const resultsRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  // Real version from driverhost (build-tag injected via ldflags). Falls
  // back to "dev" when running unbuilt (`go run ./cmd/driverhost`) since
  // that's exactly what the version variable defaults to. Empty string
  // = still loading.
  const [versionLabel, setVersionLabel] = useState("");
  useEffect(() => {
    let cancelled = false;
    fetchSystemInfo()
      .then((info) => {
        if (cancelled) return;
        setVersionLabel(info?.version ? `v${String(info.version).replace(/^v/, "")}` : "");
      })
      .catch(() => {
        if (!cancelled) setVersionLabel("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const results = query.trim()
    ? getAllNavItems()
        .filter((item) => isProductVisible(item, loading ? [] : products))
        .filter((item) => item.searchText.includes(query.trim().toLowerCase()))
        .slice(0, 8)
    : [];

  const searchOpen = results.length > 0;

  /* Reset selection when results change */
  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  /* Close search on outside click */
  useEffect(() => {
    if (!searchOpen) return;
    const onDoc = (e) => {
      if (
        searchRef.current?.contains(e.target) ||
        resultsRef.current?.contains(e.target)
      )
        return;
      setQuery("");
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [searchOpen]);

  /* Close profile on outside click */
  useEffect(() => {
    if (!profileOpen) return;
    const onDoc = (e) => {
      if (
        profileBtnRef.current?.contains(e.target) ||
        profileMenuRef.current?.contains(e.target)
      )
        return;
      setProfileOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [profileOpen]);

  /* Position profile menu */
  const updatePos = useCallback(() => {
    const btn = profileBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: `${Math.round(r.bottom + 6)}px`,
      right: `${Math.max(12, Math.round(window.innerWidth - r.right))}px`,
    });
  }, []);

  useLayoutEffect(() => {
    if (!profileOpen) return;
    updatePos();
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [profileOpen, updatePos]);

  const handleLogout = () => {
    setProfileOpen(false);
    fetch(`${CORE_URL}/auth/logout`, {
      method: "POST",
      credentials: "include",
    }).then((res) => res.json()).then((data) => {
      if (data.cognito_logout_url) {
        window.location.replace(data.cognito_logout_url);
      } else {
        window.location.replace("/gateway/?loggedOut=true");
      }
    }).catch(() => {
      window.location.replace("/gateway/?loggedOut=true");
    });
  };

  const pickResult = (item) => {
    if (onNavigate) onNavigate(item.id);
    setQuery("");
  };

  const handleSearchKey = (e) => {
    if (!searchOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (results[selectedIdx]) pickResult(results[selectedIdx]);
    } else if (e.key === "Escape") {
      setQuery("");
    }
  };

  const initial = (username || "?")[0].toUpperCase();

  return (
    <header className="gwTopbar">
      <button type="button" className="gwMenuBtn" aria-label="Toggle menu" onClick={onMenuToggle}>
        <span className="gwMenuBtnBar" />
        <span className="gwMenuBtnBar" />
        <span className="gwMenuBtnBar" />
      </button>
      <div className="gwBrand">
        <img src="/logo-white.svg" alt="Control Seat" className="gwBrandLogo" />
        <div>
          <div className="gwTitle">Gateway</div>
          <div className="gwSubtitle">
            {versionLabel ? `Control Seat ${versionLabel}` : "Control Seat"}
          </div>
        </div>
      </div>
      <div className="gwTopbarActions">
        <div className="gwSearchWrap" ref={searchRef}>
          <div className="gwSearch">
            <img src={searchIconSrc} alt="" className="gwSearchIcon" />
            <input
              placeholder="Search pages..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleSearchKey}
            />
            {query && (
              <button
                type="button"
                className="gwSearchClear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {searchOpen && (
            <div className="gwSearchResults" ref={resultsRef}>
              {results.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  className={`gwSearchResult ${i === selectedIdx ? "isSelected" : ""}`}
                  onMouseEnter={() => setSelectedIdx(i)}
                  onClick={() => pickResult(item)}
                >
                  <span className="gwSearchResultLabel">{item.label}</span>
                  <span className="gwSearchResultGroup">{item.group}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <a className="gwLink gwEditorLink" href={STUDIO_URL}>
          Back to Editor
        </a>
        {onToggleAI && (
          <button
            type="button"
            className={`gwAIButton${aiOpen ? " isActive" : ""}`}
            aria-label="AI Assistant"
            aria-pressed={Boolean(aiOpen)}
            onClick={onToggleAI}
            title="AI Assistant"
          >
            <img className="gwAIIcon" src={sparksIconSrc} alt="" />
          </button>
        )}
        <div className="gwProfileWrap">
          <button
            ref={profileBtnRef}
            type="button"
            className="gwProfileButton"
            aria-label="Open profile menu"
            aria-expanded={profileOpen}
            aria-haspopup="menu"
            onClick={() => setProfileOpen((o) => !o)}
          >
            {avatar ? (
              <img src={avatar} alt="" className="gwProfileAvatarImg" />
            ) : (
              <span className="gwProfileAvatar">{initial}</span>
            )}
          </button>
        </div>
        {/* Portaled to <body>: .gwTopbar has `backdrop-filter`, which makes
            it the containing block for position:fixed descendants. Rendered
            inline, the menu's viewport-based coords would be re-based to the
            topbar — and the AI drawer's `padding-right` on .gwShell shifts
            the topbar's edge, throwing the menu off. Portaling escapes that. */}
        {profileOpen && menuStyle && createPortal(
          <div
            className="gwProfileMenu"
            ref={profileMenuRef}
            role="menu"
            style={menuStyle}
          >
            <div className="gwProfileUser">
              {avatar ? (
                <img src={avatar} alt="" className="gwProfileAvatarImg gwProfileAvatarImgLarge" />
              ) : (
                <span className="gwProfileAvatar gwProfileAvatarLarge">
                  {initial}
                </span>
              )}
              <div>
                <div className="gwProfileName">{username || "User"}</div>
                <div className="gwProfileRole">{userProp?.role || "user"}</div>
              </div>
            </div>
            <div className="gwProfileDivider" />
            <a
              className="gwProfileItem"
              href={STUDIO_URL}
              role="menuitem"
              onClick={() => setProfileOpen(false)}
            >
              Back to Editor
            </a>
            <button
              className="gwProfileItem"
              type="button"
              role="menuitem"
              onClick={toggleTheme}
            >
              <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
              <img
                src={theme === "dark" ? sunLightIconSrc : halfMoonIconSrc}
                alt=""
                className="gwProfileThemeIcon"
              />
            </button>
            <div className="gwProfileDivider" />
            <button
              className="gwProfileItem gwProfileLogout"
              type="button"
              role="menuitem"
              onClick={handleLogout}
            >
              Log Out
            </button>
          </div>,
          document.body
        )}
      </div>
    </header>
  );
};

export { SidePanel, GatewayTopbar, PlaceholderPage };
